/**
 * Phase 9A.2 · Historical Entry — POS-aligned reconstruction workspace
 * with a multi-line CART, ticket-level payment, smart Quick Products
 * driven by real saved historical sales, and expanded Location list.
 *
 * Route: `/admin/historical-entry`
 *
 * Persistent through Save & Next: date, source (location).
 * Cleared after Save & Next: cart, payment, customer, notes.
 *
 * A single ticket may contain multiple lines (Shopify products, generic
 * historical stickers, legacy Quick Items, custom one-offs). All lines
 * are saved atomically into ONE `db.sticker_orders` record via the
 * existing manual-order backend `POST /api/admin/sticker-orders/manual`.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Search, Layers, DollarSign, Package, Star,
  Calendar, X, Sparkles, Award, AlertTriangle, ShoppingCart, Trash2,
  Minus, ClipboardList,
} from "lucide-react";

const LS = {
  date: "cu.hist.date",
  source: "cu.hist.source",           // canonical ORDER_SOURCE value
  sourceDetail: "cu.hist.sourceDetail",// optional refinement
  recentIds: "cu.hist.recentIds",
  counter: "cu.hist.counter",
};

// Phase 9A.2 · Operator-facing location list. Store / Haulover Park /
// Tropical Park lead. Each choice maps to the existing canonical
// `source` value (never invents new source strings) plus an additive
// `source_detail` for the physical location where applicable.
const SOURCE_CHOICES = [
  { key: "store",         label: "Store",         source: "in_person", detail: "store" },
  { key: "haulover_park", label: "Haulover Park", source: "event",     detail: "haulover_park" },
  { key: "tropical_park", label: "Tropical Park", source: "event",     detail: "tropical_park" },
  { key: "in_person",     label: "In-Person (Other)", source: "in_person", detail: null },
  { key: "event",         label: "Event (Other)", source: "event",     detail: null },
  { key: "phone",         label: "Phone",         source: "phone",     detail: null },
  { key: "instagram",     label: "Instagram",     source: "instagram", detail: null },
  { key: "facebook",      label: "Facebook",      source: "facebook",  detail: null },
  { key: "tiktok",        label: "TikTok",        source: "tiktok",    detail: null },
  { key: "whatsapp",      label: "WhatsApp",      source: "whatsapp",  detail: null },
  { key: "other",         label: "Other",         source: "other",     detail: null },
];

const PAYMENTS = [
  { key: "cash", label: "Cash" },
  { key: "card", label: "Card" },
  { key: "zelle", label: "Zelle" },
  { key: "cashapp", label: "Cash App" },
  { key: "wise", label: "WISE" },
  { key: "other", label: "Other" },
];

const CATEGORY_TILES = [
  { key: "stickers", label: "Single Stickers",       icon: Layers,    action: "single_stickers" },
  { key: "packs",    label: "Sticker Packs & Boxes", icon: Package,   action: "search", q: "pack" },
  { key: "albums",   label: "Albums",                icon: Award,     action: "search", q: "album" },
  { key: "sets",     label: "Complete Sets",         icon: Sparkles,  action: "search", q: "set" },
  { key: "featured", label: "Featured",              icon: Sparkles,  action: "search", q: "" },
];

const HISTORICAL_TAX_RATE = 0.07;   // Florida 7% — matches backend price-list tax rule.
const QUICK_PRODUCT_COUNT = 6;

function todayISO() { return new Date().toISOString().slice(0, 10); }
function lsGet(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : v; }
  catch { return fallback; }
}
function lsSet(key, val) {
  try { localStorage.setItem(key, val == null ? "" : String(val)); }
  catch { /* silent */ }
}
function lsGetJSON(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
  catch { return fallback; }
}
function money(n) { return `$${(Number(n) || 0).toFixed(2)}`; }
function uid() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID() : String(Date.now()) + Math.random();
}


export default function AdminHistoricalEntry() {
  const [date, setDate] = useState(() => lsGet(LS.date, todayISO()));
  const [sourceKey, setSourceKey] = useState(() => lsGet(LS.source, "store"));
  const [quickItems, setQuickItems] = useState([]);
  const [smartQuick, setSmartQuick] = useState({ results: [], empty: true });
  const [recentIds, setRecentIds] = useState(() => lsGetJSON(LS.recentIds, []));
  const [cart, setCart] = useState([]);
  const [entry, setEntry] = useState(null);           // {mode, ...} for line entry
  const [view, setView] = useState("home");          // home | cart
  const [saved, setSaved] = useState(Number(lsGet(LS.counter, "0")) || 0);
  const [saving, setSaving] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  // Feb-2026 — clickable "N saved" badge opens a read-only recent
  // entries sheet so the operator can resume where they left off.
  const [showRecent, setShowRecent] = useState(false);
  // Historical Entry tax model (Feb-2026):
  //   line prices are PRE-TAX; backend augment_snapshot adds tax on top
  //   using the active price-list rule (US-FL = 7%). Tax Exempt is an
  //   order-level override that zeros the tax without touching per-line
  //   taxable flags. Per owner directive: MUST default OFF for every
  //   new sale, MUST reset OFF after Save & Next, and MUST NOT be
  //   persisted across transactions (no localStorage).
  const [taxExempt, setTaxExempt] = useState(false);

  useEffect(() => { lsSet(LS.date, date); }, [date]);
  useEffect(() => { lsSet(LS.source, sourceKey); }, [sourceKey]);
  useEffect(() => {
    lsSet(LS.recentIds, JSON.stringify(recentIds.slice(0, 10)));
  }, [recentIds]);
  useEffect(() => { lsSet(LS.counter, saved); }, [saved]);

  const sourceChoice = useMemo(
    () => SOURCE_CHOICES.find((s) => s.key === sourceKey) || SOURCE_CHOICES[0],
    [sourceKey]
  );

  async function reloadItems() {
    try {
      const r = await api.get("/admin/historical-quick-items",
                               { params: { active: true, limit: 200 } });
      setQuickItems(r.data?.items || []);
    } catch { /* silent */ }
  }
  async function reloadSmartQuick() {
    try {
      const r = await api.get("/admin/shopify-products/historical-quick-products",
                                { params: { limit: QUICK_PRODUCT_COUNT } });
      setSmartQuick({
        results: r.data?.results || [],
        empty: !!r.data?.empty || !(r.data?.results?.length),
      });
    } catch { setSmartQuick({ results: [], empty: true }); }
  }
  useEffect(() => { reloadItems(); reloadSmartQuick(); }, []);

  const recentTiles = useMemo(() => {
    if (!quickItems.length) return [];
    const byId = new Map(quickItems.map((it) => [it.id, it]));
    return recentIds.map((id) => byId.get(id)).filter(Boolean).slice(0, 6);
  }, [recentIds, quickItems]);

  function pushRecent(id) {
    setRecentIds((prev) => [id, ...prev.filter((x) => x !== id)].slice(0, 10));
  }

  // ── Cart helpers ──────────────────────────────────────────────
  function addLine(line) {
    setCart((c) => [...c, { _cartId: uid(), ...line }]);
    toast.success(`Added · ${money(line._gross)}`, { duration: 900 });
    setEntry(null);
  }
  function removeLine(cartId) {
    setCart((c) => c.filter((l) => l._cartId !== cartId));
  }
  function updateLineQty(cartId, nextQty) {
    // Adjust qty on Shopify product / legacy quick item lines only —
    // generic_sticker + custom keep whatever the operator entered.
    setCart((c) => c.map((l) => {
      if (l._cartId !== cartId) return l;
      const q = Math.max(1, Math.floor(nextQty));
      // Lines carry a PRE-TAX per-unit `unit_price`; `_gross` is the
      // pre-tax line subtotal (unit_price × qty). Tax is computed
      // client-side in `totals` for preview and re-computed
      // authoritatively on the server at save time.
      const perUnit = Number(l.unit_price) || (Number(l._gross || 0) / (l.qty || 1));
      return { ...l, qty: q, _gross: Math.round(perUnit * q * 100) / 100 };
    }));
  }
  function updateLineTaxable(cartId, taxable) {
    setCart((c) => c.map((l) => (l._cartId === cartId ? { ...l, taxable: !!taxable } : l)));
  }

  // ── Totals (pre-tax subtotal + tax + total) ───────────────────
  // Client-side preview only. Backend `augment_snapshot(...)` is
  // authoritative on save; if the two ever diverge the persisted
  // snapshot wins. Rounding mirrors the backend `_round` (bankers-off
  // half-up via toFixed(2)).
  const totals = useMemo(() => {
    const subtotal = Math.round(cart.reduce((s, l) => s + (Number(l._gross) || 0), 0) * 100) / 100;
    const taxableSubtotal = taxExempt
      ? 0
      : Math.round(cart.reduce(
          (s, l) => s + (l.taxable === false ? 0 : (Number(l._gross) || 0)), 0) * 100) / 100;
    const tax = Math.round(taxableSubtotal * HISTORICAL_TAX_RATE * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;
    return { subtotal, tax, total };
  }, [cart, taxExempt]);
  const cartTotal = totals.total;

  // ── Final ticket save ─────────────────────────────────────────
  async function submitTicket(paymentMethod) {
    if (!cart.length) { toast.error("Cart is empty."); return; }
    if (!paymentMethod) { toast.error("Choose a payment."); return; }
    setSaving(true);
    try {
      // Split cart into product_lines[] and custom_lines[]. Strip the
      // internal `_cartId` / `_gross` bookkeeping fields — the backend
      // only wants the pricing-engine shape.
      const productLines = [];
      const customLines  = [];
      const usedQuickItemIds = new Set();
      for (const l of cart) {
        // eslint-disable-next-line no-unused-vars
        const { _cartId, _gross, _kind, _quickItemId, ...clean } = l;
        if (_kind === "shopify") productLines.push(clean);
        else customLines.push(clean);
        if (_quickItemId) usedQuickItemIds.add(_quickItemId);
      }
      const payload = {
        album_slug: "panini-fifa-world-cup-2026",
        customer: { customer_id: null },
        guest_sale: true,
        is_historical_sale: true,
        delivery: { code: "local_pickup" },
        jurisdiction: "US-FL",
        sticker_lists: [],
        product_lines: productLines,
        custom_lines: customLines,
        taxability: { entire_sale_non_taxable: !!taxExempt },
        meta: {
          source: sourceChoice.source,
          source_detail: sourceChoice.detail,
          original_order_date: `${date}T12:00:00Z`,
          payment_status: "paid",
          payment_method: paymentMethod,
          payments: [{ amount: cartTotal, method: paymentMethod }],
          receipt_preference: "suppress",
          notify_customer_suppressed: true,
          phase: "phase9a2_historical_cart",
        },
      };
      const r = await api.post("/admin/sticker-orders/manual", payload);
      if (r.data?.public_id || r.data?.ok !== false) {
        setSaved((n) => n + 1);
        toast.success(`Sale ${saved + 1} saved · ${money(cartTotal)}`);
        // Touch usage counters + push to Recent for Legacy Quick Items.
        for (const qid of usedQuickItemIds) {
          try { await api.post(`/admin/historical-quick-items/${qid}/touch`); }
          catch { /* silent */ }
          pushRecent(qid);
        }
        setCart([]);
        setTaxExempt(false);   // Tax Exempt is per-transaction only.
        setView("home");
        reloadSmartQuick();  // Smart ranking updates with new sale
        reloadItems();       // usage_count re-sort
      } else {
        toast.error("Save failed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  }

  // ── Header (persistent) ───────────────────────────────────────
  const Header = (
    <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)]"
         data-testid="hist-header">
      <div className="p-3 flex items-center gap-2">
        <a href="/admin" className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">
            Admin · Reconstruction
          </div>
          <div className="font-bold">Historical Entry</div>
        </div>
        <button type="button"
                onClick={() => setShowRecent(true)}
                className="text-xs bg-[var(--panini-red,_#dc2626)] text-white px-2 py-1 rounded-full font-bold inline-flex items-center gap-1 active:opacity-80"
                data-testid="hist-saved-count"
                aria-label="Show recent historical entries">
          <ClipboardList size={12}/> {saved} saved
        </button>
      </div>
      <div className="px-3 pb-3 grid grid-cols-2 gap-2">
        <label className="flex items-center gap-2 border-2 border-[var(--ink)] rounded-xl px-2 py-2 bg-white">
          <Calendar size={16}/>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="flex-1 outline-none text-sm" data-testid="hist-date-input"/>
        </label>
        <select value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}
                className="border-2 border-[var(--ink)] rounded-xl px-2 py-2 bg-white text-sm"
                data-testid="hist-source-select">
          {SOURCE_CHOICES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  // ── Entry sheet ──────────────────────────────────────────────
  if (entry) {
    return (
      <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-4">
        {Header}
        {entry.mode === "quick_item" && (
          <QuickItemEntry item={entry.item} onCancel={() => setEntry(null)}
                            onAdd={addLine}/>
        )}
        {entry.mode === "generic_sticker" && (
          <GenericStickerEntry onCancel={() => setEntry(null)} onAdd={addLine}/>
        )}
        {entry.mode === "shopify" && (
          <ShopifyProductEntry product={entry.product}
                                 onCancel={() => setEntry(null)} onAdd={addLine}/>
        )}
        {entry.mode === "search" && (
          <ShopifySearchView initialQ={entry.q || ""} autoFocus={!!entry.autoFocus}
                              onPick={(p) => setEntry({ mode: "shopify", product: p })}
                              onCancel={() => setEntry(null)}/>
        )}
        {entry.mode === "custom" && (
          <CustomItemEntry onCancel={() => setEntry(null)} onAdd={addLine}/>
        )}
      </div>
    );
  }

  // ── Cart view (final ticket review + payment) ────────────────
  if (view === "cart") {
    return (
      <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-4"
           data-testid="hist-cart-view">
        {Header}
        <div className="p-3 space-y-3">
          <button onClick={() => setView("home")}
                  className="flex items-center gap-1 text-sm text-[var(--smoke)]"
                  data-testid="hist-cart-back-btn">
            <ArrowLeft size={14}/> Continue adding lines
          </button>
          <div className="border-2 border-[var(--ink)] rounded-2xl bg-white divide-y"
               data-testid="hist-cart-lines">
            {cart.length === 0 && (
              <div className="p-4 text-sm text-[var(--smoke)] text-center">
                Cart is empty.
              </div>
            )}
            {cart.map((l) => (
              <CartLine key={l._cartId} line={l}
                          onDec={() => updateLineQty(l._cartId, l.qty - 1)}
                          onInc={() => updateLineQty(l._cartId, l.qty + 1)}
                          onRemove={() => removeLine(l._cartId)}
                          onToggleTaxable={(v) => updateLineTaxable(l._cartId, v)}/>
            ))}
          </div>
          {/* Totals — pre-tax Subtotal, Tax (FL 7%), Total. Backend
              `augment_snapshot` is authoritative at save time. */}
          <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-3 space-y-2"
               data-testid="hist-cart-totals">
            <div className="flex items-center justify-between text-sm">
              <span>Subtotal</span>
              <b data-testid="hist-cart-subtotal">{money(totals.subtotal)}</b>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>Tax {taxExempt ? "(exempt)" : "(FL 7%)"}</span>
              <b data-testid="hist-cart-tax">{money(totals.tax)}</b>
            </div>
            <label className="flex items-center gap-2 text-xs pt-1"
                     data-testid="hist-cart-tax-exempt-row">
              <input type="checkbox" checked={taxExempt}
                     onChange={(e) => setTaxExempt(e.target.checked)}
                     data-testid="hist-cart-tax-exempt-toggle"
                     className="w-4 h-4"/>
              <span>Tax Exempt (whole sale)</span>
            </label>
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-sm font-semibold">Total</span>
              <b className="text-[var(--panini-red,_#dc2626)] text-lg"
                 data-testid="hist-cart-total">{money(totals.total)}</b>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-1">Payment Method</div>
            <div className="grid grid-cols-2 gap-2" data-testid="hist-cart-payment-tiles">
              {PAYMENTS.map((p) => (
                <button key={p.key}
                        disabled={saving || !cart.length}
                        onClick={() => submitTicket(p.key)}
                        data-testid={`hist-cart-pay-${p.key}`}
                        className={`border-2 rounded-2xl py-4 font-bold text-white
                          ${saving || !cart.length ? "bg-gray-400 border-gray-400"
                             : "bg-[var(--panini-red,_#dc2626)] border-[var(--panini-red)]"}`}>
                  <div className="text-lg font-black" data-testid={`hist-cart-pay-${p.key}-method`}>
                    {p.label}
                  </div>
                  <div className="text-[11px] font-normal opacity-90" data-testid={`hist-cart-pay-${p.key}-cta`}>
                    Save &amp; Next
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Home ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-24" data-testid="hist-home">
      {Header}
      <div className="p-3 space-y-4">
        <button onClick={() => setEntry({ mode: "search", autoFocus: true })}
                className="w-full flex items-center gap-2 border-2 border-[var(--ink)] rounded-2xl px-4 py-3 bg-white"
                data-testid="hist-search-btn">
          <Search size={18}/>
          <span className="text-[var(--smoke)]">Search products…</span>
        </button>

        {/* Smart Quick Products — ONLY from real completed historical sales. */}
        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1 flex items-center gap-1">
            <Sparkles size={11}/> Quick Products
            <span className="ml-1 text-[9px] normal-case text-[var(--smoke)]">
              (most-sold historically)
            </span>
          </div>
          {smartQuick.empty ? (
            <div className="text-xs text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
                 data-testid="hist-smart-quick-empty">
              Your most-sold Shopify products will appear here as you enter
              historical sales.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="hist-quick-products">
              {smartQuick.results.map((p, i) => (
                <button key={p.id || i}
                        onClick={() => setEntry({ mode: "shopify", product: p })}
                        className="border-2 border-[var(--ink)] rounded-xl p-2 bg-white flex flex-col items-start gap-1 min-h-[92px] active:bg-red-50"
                        data-testid={`hist-quick-product-${i}`}>
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-full h-10 object-contain"/>
                  ) : (
                    <div className="w-full h-10 bg-gray-100 rounded flex items-center justify-center text-gray-400">
                      <Package size={16}/>
                    </div>
                  )}
                  <div className="text-[11px] font-semibold leading-tight line-clamp-2 text-left w-full">
                    {p.title}
                  </div>
                  <div className="text-[10px] text-[var(--smoke)]">
                    {p.total_qty_sold} sold
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Categories — same taxonomy as Quick Sale. */}
        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)] mb-1 flex items-center gap-1">
            <Layers size={11}/> Categories
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="hist-categories">
            {CATEGORY_TILES.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key}
                        onClick={() => {
                          if (t.action === "single_stickers")
                            setEntry({ mode: "generic_sticker" });
                          else
                            setEntry({ mode: "search", q: t.q || "", autoFocus: false });
                        }}
                        className="border-2 border-[var(--ink)] rounded-2xl p-4 bg-white flex flex-col items-start gap-2 min-h-[86px] active:bg-red-50"
                        data-testid={`hist-cat-${t.key}`}>
                  <Icon size={20} className="text-[var(--panini-red)]"/>
                  <span className="font-bold text-sm text-left">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Legacy / Quick Items */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase text-[var(--smoke)] flex items-center gap-1">
              <Star size={11}/> Quick Items
              <span className="ml-1 text-[9px] normal-case text-[var(--smoke)] font-normal">
                (non-Shopify · shared)
              </span>
            </div>
            <button onClick={() => setShowAddItem(true)}
                    className="text-xs text-[var(--panini-red)] font-semibold flex items-center gap-1"
                    data-testid="hist-add-quick-item-btn">
              <Plus size={12}/> Add
            </button>
          </div>
          {recentTiles.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Recent</div>
              <div className="grid grid-cols-3 gap-2" data-testid="hist-recent-tiles">
                {recentTiles.map((it) => (
                  <QuickItemTile key={it.id} item={it}
                                    onClick={() => setEntry({ mode: "quick_item", item: it })}/>
                ))}
              </div>
            </div>
          )}
          {quickItems.length === 0 ? (
            <div className="text-xs text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
                 data-testid="hist-quick-items-empty">
              No Quick Items yet. Tap <b>Add</b> to save a reusable entry
              (Cajita, Jersey, Bucket Hat, …). Shared with Quick Sale and
              Sales Orders. For discontinued or non-Shopify merchandise only.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2" data-testid="hist-quick-items-grid">
              {quickItems.map((it) => (
                <QuickItemTile key={it.id} item={it}
                                  onClick={() => setEntry({ mode: "quick_item", item: it })}/>
              ))}
            </div>
          )}
        </div>

        {/* Custom one-off */}
        <button onClick={() => setEntry({ mode: "custom" })}
                className="w-full border-2 border-dashed border-[var(--ink)] rounded-2xl p-3 bg-white text-left active:bg-red-50"
                data-testid="hist-action-custom">
          <div className="flex items-center gap-2 text-[var(--panini-red)]">
            <DollarSign size={18}/>
            <span className="font-bold text-sm">Custom Item</span>
          </div>
          <div className="text-[11px] text-[var(--smoke)] mt-1">
            One-off historical line · free-text description
          </div>
        </button>
      </div>

      {/* Sticky Cart bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t-2 border-[var(--ink)] bg-white px-3 py-3"
           data-testid="hist-cart-bar">
        <button onClick={() => setView("cart")}
                disabled={!cart.length}
                className={`w-full flex items-center justify-between rounded-2xl px-4 py-3 font-bold text-white ${
                  cart.length ? "bg-[var(--panini-red,_#dc2626)]" : "bg-gray-400"
                }`}
                data-testid="hist-view-cart-btn">
          <span className="flex items-center gap-2"><ShoppingCart size={18}/> Sale</span>
          <span>{cart.length} line{cart.length === 1 ? "" : "s"} · {money(cartTotal)}</span>
        </button>
      </div>

      {showAddItem && (
        <AddQuickItemModal onClose={() => setShowAddItem(false)}
                              onCreated={async () => {
                                setShowAddItem(false); await reloadItems();
                              }}/>
      )}
      {showRecent && (
        <RecentEntriesSheet onClose={() => setShowRecent(false)}/>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Recent Historical Entries — read-only bottom sheet
// ═══════════════════════════════════════════════════════════════
// Reads from `db.sticker_orders` via /api/admin/historical-entry/recent
// so the list survives session/device switches (never localStorage).
// Tapped from the header "N saved" badge. No fulfillment actions —
// audit view only.
function RecentEntriesSheet({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  // Feb-2026 — Edit Date sub-view for a single row. When set, the
  // sheet swaps its content for the date-picker UX. Save flips this
  // back to null and refreshes the affected row in place.
  const [editing, setEditing] = useState(null);   // row object | null

  async function _loadPage(cursor) {
    try {
      const r = await api.get("/admin/historical-entry/recent", {
        params: { limit: 20, ...(cursor ? { cursor } : {}) },
      });
      const nextRows = r.data?.items || [];
      // De-dupe defensively — cursor pagination should never overlap
      // but any race with a fresh Save & Next while the sheet is
      // open could otherwise show the same row twice.
      setRows((prev) => {
        if (!cursor) return nextRows;
        const seen = new Set(prev.map((x) => x.public_id));
        return [...prev, ...nextRows.filter((x) => !seen.has(x.public_id))];
      });
      setHasMore(!!r.data?.has_more);
      setNextCursor(r.data?.next_cursor || null);
    } catch (e) {
      toast.error("Could not load recent entries.");
    }
  }

  useEffect(() => {
    (async () => {
      await _loadPage(null);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function _onLoadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try { await _loadPage(nextCursor); }
    finally { setLoadingMore(false); }
  }

  function _fmtHistDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleDateString(undefined,
      { month: "short", day: "numeric", year: "numeric" });
  }
  function _fmtEnteredAt(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString(undefined,
      { hour: "numeric", minute: "2-digit" });
    return sameDay
      ? `Entered ${time}`
      : `Entered ${d.toLocaleDateString(undefined,
          { month: "short", day: "numeric" })} · ${time}`;
  }
  function _sourceLabel(row) {
    const s = row.source_detail
      ? row.source_detail.replace(/_/g, " ")
      : (row.source || "").replace(/_/g, " ");
    return s ? s.replace(/\b\w/g, (c) => c.toUpperCase()) : "—";
  }
  function _payLabel(m) {
    if (!m) return "";
    const map = { cash: "Cash", card: "Card", zelle: "Zelle",
                    cashapp: "Cash App", wise: "WISE", other: "Other" };
    return map[m] || m;
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-end sm:items-center justify-center"
         onClick={onClose} data-testid="hist-recent-sheet">
      <div className="bg-white w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] max-h-[85vh] overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-3 flex items-center justify-between border-b-2 border-[var(--ink)]">
          <div>
            <div className="text-[10px] uppercase text-[var(--smoke)]">
              Read-only · resume where you left off
            </div>
            <div className="font-bold">Recent Historical Entries</div>
          </div>
          <button onClick={onClose} data-testid="hist-recent-close" className="p-1">
            <X size={20}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto divide-y">
          {loading && (
            <div className="p-6 text-center text-sm text-[var(--smoke)]"
                 data-testid="hist-recent-loading">
              Loading…
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center text-sm text-[var(--smoke)]"
                 data-testid="hist-recent-empty">
              No Historical Entry transactions saved yet. Your recent
              entries will appear here once you Save & Next a sale.
            </div>
          )}
          {rows.map((r) => (
            <div key={r.public_id} className="p-3 space-y-1"
                 data-testid={`hist-recent-row-${r.public_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold">
                  {_fmtHistDate(r.submitted_at)} · {_sourceLabel(r)}
                </div>
                <div className="text-sm font-bold text-[var(--panini-red)]">
                  {money(r.total)}
                </div>
              </div>
              <div className="text-sm text-[var(--ink)] line-clamp-1">
                {r.summary || "—"}
              </div>
              <div className="text-[11px] text-[var(--smoke)] flex items-center justify-between gap-2">
                <span>{_payLabel(r.payment_method) || "—"}</span>
                <span className="flex items-center gap-2">
                  <span>{_fmtEnteredAt(r.entered_at || r.created_at)}</span>
                  <button type="button"
                          onClick={() => setEditing(r)}
                          className="text-[var(--panini-red,_#dc2626)] underline underline-offset-2 font-semibold"
                          data-testid={`hist-recent-edit-date-${r.public_id}`}>
                    Edit Date
                  </button>
                </span>
              </div>
            </div>
          ))}
          {!loading && rows.length > 0 && (
            <div className="p-3 text-center" data-testid="hist-recent-footer">
              {hasMore ? (
                <button type="button" onClick={_onLoadMore}
                        disabled={loadingMore}
                        className="w-full border-2 border-[var(--ink)] rounded-2xl py-3 font-bold disabled:opacity-60"
                        data-testid="hist-recent-load-more">
                  {loadingMore ? "Loading…" : "Load More"}
                </button>
              ) : (
                <span className="text-[11px] text-[var(--smoke)]"
                      data-testid="hist-recent-no-more">
                  No more entries.
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {editing && (
        <EditDateSheet row={editing}
                          onCancel={() => setEditing(null)}
                          onSaved={(updated) => {
                            // In-place refresh — bump the corrected
                            // row's submitted_at without changing
                            // ORDER (entered_at is unchanged).
                            setRows((prev) => prev.map((x) =>
                              x.public_id === updated.public_id
                                ? { ...x, submitted_at: updated.submitted_at }
                                : x));
                            setEditing(null);
                          }}/>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Edit Historical Sale Date — targeted correction UI
// ═══════════════════════════════════════════════════════════════
// Only `submitted_at` is corrected. entered_at, created_at,
// financial totals, items, payments, source and customer are all
// preserved verbatim by the backend PATCH endpoint.
function EditDateSheet({ row, onCancel, onSaved }) {
  const isoToDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");
  const [date, setDate] = useState(isoToDateInput(row.submitted_at));
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!date) return;
    setSaving(true);
    try {
      const res = await api.patch(
        `/admin/historical-entry/${row.public_id}/submitted-at`,
        { submitted_at: date }
      );
      toast.success("Date corrected.");
      onSaved({ public_id: row.public_id,
                 submitted_at: res.data?.submitted_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not correct date.");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
         onClick={onCancel} data-testid="hist-edit-date-sheet">
      <div className="bg-white w-full sm:max-w-md sm:mx-4 rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b-2 border-[var(--ink)]">
          <div className="text-[10px] uppercase text-[var(--smoke)]">
            Order {row.public_id}
          </div>
          <div className="font-bold">Edit Historical Sale Date</div>
        </div>
        <div className="p-3 space-y-3">
          <label className="block">
            <span className="text-sm font-semibold">Historical Sale Date</span>
            <input type="date" value={date}
                   onChange={(e) => setDate(e.target.value)}
                   className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base font-bold"
                   data-testid="hist-edit-date-input"/>
          </label>
          <div className="text-[11px] text-[var(--smoke)]">
            Only the historical sale date is updated. Items, totals,
            tax, payments, entered time and audit history are
            preserved.
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={onCancel} disabled={saving}
                       className="flex-1 border-2 border-[var(--ink)] rounded-2xl py-3 font-bold"
                       data-testid="hist-edit-date-cancel">
              Cancel
            </button>
            <button onClick={submit}
                       disabled={saving || !date
                                    || date === isoToDateInput(row.submitted_at)}
                       className="flex-1 border-2 border-[var(--panini-red)] bg-[var(--panini-red,_#dc2626)] text-white rounded-2xl py-3 font-bold disabled:opacity-50"
                       data-testid="hist-edit-date-save">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Cart line row
// ═══════════════════════════════════════════════════════════════
function CartLine({ line, onDec, onInc, onRemove, onToggleTaxable }) {
  const isTaxable = line.taxable !== false;
  return (
    <div className="p-3 flex items-center gap-2" data-testid="hist-cart-line">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold line-clamp-1">
          {line.title || line.description}
        </div>
        {line._kind === "shopify" && (
          <div className="text-[10px] text-[var(--smoke)]">
            Shopify · {line.shopify_variant_id || line.shopify_product_id || ""}
          </div>
        )}
        {line._kind === "quick_item" && (
          <div className="text-[10px] text-[var(--smoke)]">Quick Item</div>
        )}
        {line._kind === "generic_sticker" && (
          <div className="text-[10px] text-[var(--smoke)]">
            Generic Stickers · {line.sticker_quantity_known
                                   ? `${line.sticker_quantity} sticker${line.sticker_quantity === 1 ? "" : "s"}`
                                   : "qty unknown"}
          </div>
        )}
        {line._kind === "custom" && (
          <div className="text-[10px] text-[var(--smoke)]">Custom · one-off</div>
        )}
        <label className="text-[10px] mt-0.5 inline-flex items-center gap-1 opacity-80"
                 data-testid="hist-cart-line-tax-row">
          <input type="checkbox" checked={isTaxable}
                 onChange={(e) => onToggleTaxable(e.target.checked)}
                 data-testid="hist-cart-line-tax-toggle"/>
          <span>Taxable</span>
        </label>
      </div>
      <div className="flex items-center border rounded-xl">
        <button onClick={onDec} className="p-2" data-testid="hist-cart-line-dec"><Minus size={14}/></button>
        <span className="px-2 text-sm w-8 text-center">{line.qty}</span>
        <button onClick={onInc} className="p-2" data-testid="hist-cart-line-inc"><Plus size={14}/></button>
      </div>
      <div className="text-sm font-bold w-16 text-right">{money(line._gross)}</div>
      <button onClick={onRemove} className="text-red-600 p-1" data-testid="hist-cart-line-remove">
        <Trash2 size={14}/>
      </button>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Tiles + shared UI
// ═══════════════════════════════════════════════════════════════
function QuickItemTile({ item, onClick }) {
  return (
    <button onClick={onClick}
            className="border-2 border-[var(--ink)] rounded-xl p-2 bg-white text-left active:bg-red-50 min-h-[72px]"
            data-testid={`hist-quick-item-${item.id}`}>
      <div className="text-sm font-semibold line-clamp-2">{item.name}</div>
      {item.default_total != null && (
        <div className="text-[11px] text-[var(--panini-red)] mt-1"
             title="Default collected amount for 1 item">
          Default {money(item.default_total)}
        </div>
      )}
      {!!item.category && (
        <div className="text-[10px] text-[var(--smoke)] uppercase mt-1">{item.category}</div>
      )}
    </button>
  );
}

function EntryHeader({ title, subtitle, onCancel }) {
  return (
    <div className="p-3 flex items-start gap-2">
      <button onClick={onCancel} className="p-1" data-testid="hist-entry-cancel">
        <X size={20}/>
      </button>
      <div className="flex-1">
        <div className="text-[10px] uppercase text-[var(--smoke)]">{subtitle}</div>
        <div className="font-bold text-base">{title}</div>
      </div>
    </div>
  );
}

function AddToSaleButton({ disabled, onClick, testid }) {
  return (
    <button onClick={onClick} disabled={disabled}
            data-testid={testid}
            className={`w-full rounded-2xl py-4 font-bold text-lg text-white ${
              disabled ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
            }`}>
      ADD TO SALE
    </button>
  );
}


// ═══════════════════════════════════════════════════════════════
// A. Shopify search view
// ═══════════════════════════════════════════════════════════════
function ShopifySearchView({ initialQ, autoFocus, onPick, onCancel }) {
  const [q, setQ] = useState(initialQ);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/shopify-products/search",
                                  { params: { q, limit: 30 } });
        if (alive) setRows(r.data?.results || []);
      } catch { /* silent */ }
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  return (
    <div className="p-3 space-y-3" data-testid="hist-shopify-search">
      <EntryHeader title="Shopify Products" subtitle="Real inventory · Historical"
                    onCancel={onCancel}/>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" size={18}/>
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus={autoFocus}
               placeholder="title · SKU · handle"
               className="w-full border-2 border-[var(--ink)] rounded-2xl pl-10 pr-3 py-3 text-base"
               data-testid="hist-shopify-search-input"/>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {rows.map((p, i) => (
          <button key={p.id || i} onClick={() => onPick(p)}
                  className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white flex flex-col items-start gap-1 min-h-[128px] active:bg-red-50"
                  data-testid={`hist-shopify-result-${i}`}>
            {p.image_url ? (
              <img src={p.image_url} alt="" className="w-full h-16 object-contain"/>
            ) : (
              <div className="w-full h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                <Package size={22}/>
              </div>
            )}
            <div className="text-sm font-semibold leading-tight line-clamp-2 text-left">
              {p.title}
            </div>
            <div className="text-xs text-[var(--panini-red)] font-bold">
              {money(p.unit_price)}
            </div>
          </button>
        ))}
      </div>
      {rows.length === 0 && q && (
        <div className="text-sm text-[var(--smoke)] text-center py-6">
          No products matched "{q}".
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// B. Shopify product historical entry → Add to Sale
// ═══════════════════════════════════════════════════════════════
function ShopifyProductEntry({ product, onCancel, onAdd }) {
  // Historical Entry mobile-input fix (Feb-2026): keep qty as an
  // editable string so the user can transiently clear the field on
  // mobile ("" is allowed while editing) and type the new value
  // without an aggressive `|| 1` coercion racing them on every
  // keystroke. Blur + submit coerce back to an integer ≥ 1.
  const [qty, setQty] = useState("1");
  const qtyInt = Math.max(1, Math.floor(Number(qty) || 0) || 1);
  // Pre-tax mode: prefill from Shopify unit price × qty (no tax hint).
  // The Shopify mirror already stores pre-tax prices; we submit them
  // verbatim and let the backend add 7% FL tax on top.
  const prefill = useMemo(() => {
    const base = Number(product.unit_price || 0) * qtyInt;
    return (Math.round(base * 100) / 100).toFixed(2);
  }, [product.unit_price, qtyInt]);
  const [total, setTotal] = useState(prefill);
  const [lastPrefill, setLastPrefill] = useState(prefill);
  useEffect(() => {
    if (total === lastPrefill) setTotal(prefill);
    setLastPrefill(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);
  // Honor the Shopify mirror's `taxable` flag (default TRUE only when
  // the field is missing). Operator can still override per-line in the
  // cart via the Taxable checkbox.
  const initialTaxable = product.taxable !== false;
  const [taxable, setTaxable] = useState(initialTaxable);

  const gross = Math.round(Number(total || 0) * 100) / 100;

  function submit() {
    if (!(gross > 0)) return;
    const q = Math.max(1, Math.floor(qty));
    onAdd({
      _kind: "shopify",
      _gross: gross,
      id: product.id,
      shopify_product_id: product.shopify_product_id,
      shopify_variant_id: product.shopify_variant_id || undefined,
      sku: product.sku || undefined,
      title: product.title,
      variant_title: product.variant_title || undefined,
      handle: product.handle || undefined,
      qty: qtyInt,
      unit_price: Math.round((gross / qtyInt) * 100) / 100,
      taxable,
    });
  }

  return (
    <div data-testid="hist-entry-shopify">
      <EntryHeader title={product.title}
                    subtitle="Shopify Product · Historical · Pre-Tax"
                    onCancel={onCancel}/>
      <div className="p-3 space-y-3">
        <div className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white flex items-center gap-3">
          {product.image_url ? (
            <img src={product.image_url} alt="" className="w-14 h-14 object-contain"/>
          ) : (
            <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center text-gray-400">
              <Package size={20}/>
            </div>
          )}
          <div className="flex-1">
            <div className="font-semibold text-sm line-clamp-2">{product.title}</div>
            <div className="text-[11px] text-[var(--smoke)]">
              Current pre-tax price {money(product.unit_price)}
              {product.sku ? ` · SKU ${product.sku}` : ""}
              {product.taxable === false ? " · non-taxable" : ""}
            </div>
          </div>
        </div>

        <label className="block">
          <span className="text-sm font-semibold">Quantity</span>
          <input type="number" min="1" step="1" value={qty}
                 onChange={(e) => setQty(e.target.value)}
                 onBlur={() => {
                   const n = Math.floor(Number(qty));
                   setQty(String(n >= 1 ? n : 1));
                 }}
                 inputMode="numeric"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                 data-testid="hist-shopify-qty"/>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Pre-Tax Price</span>
          <input type="number" min="0" step="0.01" value={total}
                 onChange={(e) => setTotal(e.target.value)}
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                 data-testid="hist-shopify-total"/>
          <span className="mt-1 block text-[11px] text-[var(--smoke)]">
            Prefilled from current pre-tax price. Overwrite if the paper says otherwise.
            FL 7% tax is added at the totals section unless the sale is Tax Exempt.
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm" data-testid="hist-shopify-taxable-row">
          <input type="checkbox" checked={taxable}
                 onChange={(e) => setTaxable(e.target.checked)}
                 data-testid="hist-shopify-taxable"
                 className="w-4 h-4"/>
          <span>Taxable</span>
        </label>
        <AddToSaleButton testid="hist-shopify-add-btn"
                            disabled={!(gross > 0)} onClick={submit}/>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// C. Legacy Quick Item entry → Add to Sale
// ═══════════════════════════════════════════════════════════════
function QuickItemEntry({ item, onCancel, onAdd }) {
  // Historical Entry mobile-input fix (Feb-2026): editable string.
  const [qty, setQty] = useState("1");
  const qtyInt = Math.max(1, Math.floor(Number(qty) || 0) || 1);
  const suggested = useMemo(() => {
    if (item.default_total == null) return "";
    const s = Number(item.default_total) * qtyInt;
    return (Math.round(s * 100) / 100).toFixed(2);
  }, [item.default_total, qtyInt]);
  const [total, setTotal] = useState(suggested);
  const [lastSuggested, setLastSuggested] = useState(suggested);
  useEffect(() => {
    if (total === lastSuggested) setTotal(suggested);
    setLastSuggested(suggested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested]);
  const [note, setNote] = useState("");
  const [taxable, setTaxable] = useState(true);

  const gross = Math.round(Number(total || 0) * 100) / 100;

  function submit() {
    if (!(gross > 0)) return;
    onAdd({
      _kind: "quick_item",
      _gross: gross,
      _quickItemId: item.id,
      description: item.name,
      qty: qtyInt,
      unit_price: Math.round((gross / qtyInt) * 100) / 100,
      taxable,
      kind: "historical_quick_item",
      historical_quick_item_id: item.id,
      historical_quick_item_name: item.name,
      historical_quick_item_category: item.category || undefined,
      historical_note: note.trim() || undefined,
    });
  }

  return (
    <div data-testid="hist-entry-quick-item">
      <EntryHeader title={item.name} subtitle="Quick Item · Pre-Tax"
                    onCancel={onCancel}/>
      <div className="p-3 space-y-3">
        <label className="block">
          <span className="text-sm font-semibold">Quantity</span>
          <input type="number" min="1" step="1" value={qty}
                 onChange={(e) => setQty(e.target.value)}
                 onBlur={() => {
                   const n = Math.floor(Number(qty));
                   setQty(String(n >= 1 ? n : 1));
                 }}
                 inputMode="numeric"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                 data-testid="hist-qi-qty"/>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Pre-Tax Price</span>
          <input type="number" min="0" step="0.01" value={total}
                 onChange={(e) => setTotal(e.target.value)}
                 placeholder="e.g. 10.00"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                 data-testid="hist-qi-total"/>
          {item.default_total != null && (
            <span className="mt-1 block text-[11px] text-[var(--smoke)]"
                  data-testid="hist-qi-suggested-hint">
              Suggested {money(item.default_total)} × {qty} = {money(suggested)} pre-tax.
            </span>
          )}
        </label>
        <label className="flex items-center gap-2 text-sm" data-testid="hist-qi-taxable-row">
          <input type="checkbox" checked={taxable}
                 onChange={(e) => setTaxable(e.target.checked)}
                 data-testid="hist-qi-taxable"
                 className="w-4 h-4"/>
          <span>Taxable</span>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Optional Note</span>
          <input value={note} onChange={(e) => setNote(e.target.value)}
                 placeholder='e.g. "paper sheet June 8"'
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
                 data-testid="hist-qi-note"/>
        </label>
        <AddToSaleButton testid="hist-qi-add-btn"
                            disabled={!(gross > 0)} onClick={submit}/>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// D. Generic Historical Single Stickers → Add to Sale
// ═══════════════════════════════════════════════════════════════
const PRESETS = [1, 2, 3, 5, 10];

function GenericStickerEntry({ onCancel, onAdd }) {
  const [amount, setAmount] = useState(null);
  const [custom, setCustom] = useState("");
  const [qtyKnown, setQtyKnown] = useState(false);
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [taxable, setTaxable] = useState(true);
  const gross = amount != null ? amount : (Math.round(Number(custom || 0) * 100) / 100);

  function submit() {
    if (!(gross > 0)) return;
    const stickerQty = qtyKnown ? Math.max(1, Math.floor(Number(qty) || 0)) : null;
    if (qtyKnown && !stickerQty) return;
    onAdd({
      _kind: "generic_sticker",
      _gross: gross,
      description: stickerQty
        ? `Single Stickers — Historical · ${stickerQty} sticker${stickerQty === 1 ? "" : "s"}`
        : "Single Stickers — Historical · Qty unknown",
      qty: 1,
      unit_price: gross,
      taxable,
      kind: "generic_sticker",
      sticker_quantity_known: qtyKnown,
      sticker_quantity: stickerQty,
      historical_note: note.trim() || undefined,
    });
  }

  return (
    <div data-testid="hist-entry-generic-sticker">
      <EntryHeader title="Single Stickers — Historical"
                    subtitle="Revenue-only · Pre-Tax · Codes unknown"
                    onCancel={onCancel}/>
      <div className="p-3 space-y-3">
        <div>
          <div className="text-sm font-semibold mb-1">Pre-Tax Price</div>
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((v) => (
              <button key={v} onClick={() => { setAmount(v); setCustom(""); }}
                      data-testid={`hist-gs-amount-${v}`}
                      className={`border-2 rounded-2xl py-3 font-bold text-lg ${
                        amount === v ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                                       : "border-[var(--ink)] bg-white text-[var(--ink)]"
                      }`}>${v}</button>
            ))}
            <button onClick={() => setAmount(null)}
                    data-testid="hist-gs-amount-custom-btn"
                    className={`border-2 rounded-2xl py-3 font-semibold text-sm ${
                      amount === null ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                                          : "border-[var(--ink)] bg-white text-[var(--ink)]"
                    }`}>Custom</button>
          </div>
          {amount === null && (
            <input type="number" min="0" step="0.01" value={custom}
                   onChange={(e) => setCustom(e.target.value)}
                   placeholder="Enter amount"
                   className="mt-2 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                   data-testid="hist-gs-custom-input"/>
          )}
        </div>
        <div>
          <div className="text-sm font-semibold mb-1">Sticker Quantity</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => { setQtyKnown(false); setQty(""); }}
                    data-testid="hist-gs-qty-unknown"
                    className={`border-2 rounded-xl py-2 font-semibold ${
                      !qtyKnown ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                                 : "border-[var(--ink)] bg-white text-[var(--ink)]"
                    }`}>Unknown</button>
            <button onClick={() => setQtyKnown(true)}
                    data-testid="hist-gs-qty-known"
                    className={`border-2 rounded-xl py-2 font-semibold ${
                      qtyKnown ? "bg-[var(--panini-red,_#dc2626)] text-white border-[var(--panini-red)]"
                               : "border-[var(--ink)] bg-white text-[var(--ink)]"
                    }`}>Known</button>
          </div>
          {qtyKnown && (
            <input type="number" min="1" step="1" value={qty}
                   onChange={(e) => setQty(e.target.value)}
                   placeholder="How many stickers?"
                   className="mt-2 w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2"
                   data-testid="hist-gs-qty-input"/>
          )}
        </div>
        <input value={note} onChange={(e) => setNote(e.target.value)}
               placeholder='Optional note · e.g. "paper sheet June 8"'
               className="w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
               data-testid="hist-gs-note"/>
        <label className="flex items-center gap-2 text-sm" data-testid="hist-gs-taxable-row">
          <input type="checkbox" checked={taxable}
                 onChange={(e) => setTaxable(e.target.checked)}
                 data-testid="hist-gs-taxable"
                 className="w-4 h-4"/>
          <span>Taxable</span>
        </label>
        <AddToSaleButton testid="hist-gs-add-btn"
                            disabled={!(gross > 0) || (qtyKnown && !(Number(qty) > 0))}
                            onClick={submit}/>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// E. Custom one-off → Add to Sale
// ═══════════════════════════════════════════════════════════════
function CustomItemEntry({ onCancel, onAdd }) {
  const [name, setName] = useState("");
  // Historical Entry mobile-input fix (Feb-2026): editable string.
  const [qty, setQty] = useState("1");
  const qtyInt = Math.max(1, Math.floor(Number(qty) || 0) || 1);
  const [total, setTotal] = useState("");
  const [taxable, setTaxable] = useState(true);
  const gross = Math.round(Number(total || 0) * 100) / 100;

  function submit() {
    if (!name.trim() || !(gross > 0)) return;
    onAdd({
      _kind: "custom",
      _gross: gross,
      description: name.trim(),
      qty: qtyInt,
      unit_price: Math.round((gross / qtyInt) * 100) / 100,
      taxable,
    });
  }

  return (
    <div data-testid="hist-entry-custom">
      <EntryHeader title="Custom Item" subtitle="One-off · Pre-Tax" onCancel={onCancel}/>
      <div className="p-3 space-y-3">
        <label className="block">
          <span className="text-sm font-semibold">Description</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Autographed poster"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
                 data-testid="hist-custom-name"/>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Quantity</span>
          <input type="number" min="1" step="1" value={qty}
                 onChange={(e) => setQty(e.target.value)}
                 onBlur={() => {
                   const n = Math.floor(Number(qty));
                   setQty(String(n >= 1 ? n : 1));
                 }}
                 inputMode="numeric"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                 data-testid="hist-custom-qty"/>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Pre-Tax Price</span>
          <input type="number" min="0" step="0.01" value={total}
                 onChange={(e) => setTotal(e.target.value)}
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-lg font-bold"
                 data-testid="hist-custom-total"/>
        </label>
        <label className="flex items-center gap-2 text-sm" data-testid="hist-custom-taxable-row">
          <input type="checkbox" checked={taxable}
                 onChange={(e) => setTaxable(e.target.checked)}
                 data-testid="hist-custom-taxable"
                 className="w-4 h-4"/>
          <span>Taxable</span>
        </label>
        <AddToSaleButton testid="hist-custom-add-btn"
                            disabled={!name.trim() || !(gross > 0)} onClick={submit}/>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Add Legacy Quick Item modal
// ═══════════════════════════════════════════════════════════════
function AddQuickItemModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [defTotal, setDefTotal] = useState("");
  const [saving, setSaving] = useState(false);
  const [dupe, setDupe] = useState(null);
  const [checkingDupe, setCheckingDupe] = useState(false);

  useEffect(() => {
    const n = name.trim();
    if (!n) { setDupe(null); return; }
    let alive = true;
    setCheckingDupe(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/historical-quick-items/preflight",
                                  { params: { name: n } });
        if (alive) setDupe(r.data?.shopify_match || null);
      } catch { if (alive) setDupe(null); }
      finally { if (alive) setCheckingDupe(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); setCheckingDupe(false); };
  }, [name]);

  async function create() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/historical-quick-items", {
        name: name.trim(),
        category: category.trim() || null,
        default_total: defTotal ? Number(defTotal) : null,
      });
      toast.success(r.data?.created ? "Quick Item created." : "Quick Item exists — reactivated.");
      onCreated(r.data?.item);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end sm:items-center justify-center"
         data-testid="hist-add-item-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-bold">+ Add Quick Item</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="text-[11px] text-[var(--smoke)]">
          For discontinued or non-Shopify merchandise only. If it exists in
          Shopify, use the Shopify product instead so sales stay grouped.
        </div>
        <label className="block">
          <span className="text-sm font-semibold">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Cajita"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-2xl px-3 py-3 text-base"
                 data-testid="hist-add-name"/>
        </label>
        {dupe && (
          <div className="border-2 border-yellow-400 bg-yellow-50 rounded-xl p-2 text-xs flex items-start gap-2"
               data-testid="hist-add-shopify-dupe-warning">
            <AlertTriangle size={14} className="text-yellow-700 mt-0.5 flex-shrink-0"/>
            <div>
              <div className="font-semibold text-yellow-900">
                "{dupe.title}" already exists in Shopify.
              </div>
              <div className="text-yellow-800 mt-0.5">
                Use the Shopify product so lifetime sales stay grouped
                together. Only proceed here if this is truly a
                different / discontinued item.
              </div>
            </div>
          </div>
        )}
        {checkingDupe && !dupe && (
          <div className="text-[10px] text-[var(--smoke)]">Checking Shopify catalog…</div>
        )}
        <label className="block">
          <span className="text-sm font-semibold">Category (optional)</span>
          <input value={category} onChange={(e) => setCategory(e.target.value)}
                 placeholder="e.g. Merch"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
                 data-testid="hist-add-category"/>
        </label>
        <label className="block">
          <span className="text-sm font-semibold">Default collected amount for 1 item (optional)</span>
          <input type="number" min="0" step="0.01" value={defTotal}
                 onChange={(e) => setDefTotal(e.target.value)}
                 placeholder="e.g. 20.00"
                 className="mt-1 w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
                 data-testid="hist-add-default-total"/>
          <span className="mt-1 block text-[10px] text-[var(--smoke)]">
            Prefills Total Collected when Qty = 1. Not an authoritative
            unit sale price — always editable.
          </span>
        </label>
        <button onClick={create} disabled={!name.trim() || saving}
                data-testid="hist-add-save-btn"
                className={`w-full rounded-2xl py-3 font-bold text-white ${
                  !name.trim() || saving ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                }`}>
          {saving ? "Saving…" : "Save Quick Item"}
        </button>
      </div>
    </div>
  );
}
